import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getMySubmissions } from '../api/submissions'
import type { CrowdSubmission } from '../types'
import Spinner from '../components/common/Spinner'

export default function MySubmissionsPage() {
  const { t } = useTranslation(['worker', 'common'])
  const navigate = useNavigate()
  const [submissions, setSubmissions] = useState<CrowdSubmission[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getMySubmissions()
      .then(({ items, total }) => {
        setSubmissions(items)
        setTotal(total)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : t('common:error.failedToLoad')))
      .finally(() => setIsLoading(false))
  }, [t])

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; bg: string; color: string }> = {
      pending: { label: t('common:status.pending', { ns: 'common' }), bg: '#f59e0b22', color: '#f59e0b' },
      verified: { label: t('common:status.verified', { ns: 'common' }), bg: '#22c55e22', color: '#22c55e' },
      rejected: { label: t('common:status.rejected', { ns: 'common' }), bg: '#ef444422', color: '#ef4444' },
    }
    return map[status] ?? map.pending
  }

  return (
    <div className="min-h-screen pb-8 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      <div className="p-4 space-y-3">
        <button
          className="text-sm opacity-60"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(-1)}
        >
          {t('common:back', { ns: 'common' })}
        </button>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('mySubmissions.title')}
          </h1>
          {total > 0 && (
            <span className="text-sm opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('mySubmissions.total', { count: total })}
            </span>
          )}
        </div>

        {isLoading && <Spinner />}

        {error && (
          <div className="rounded-xl px-4 py-3 text-sm" style={{ background: '#ef444418', color: '#ef4444' }}>
            {error}
          </div>
        )}

        {!isLoading && !error && submissions.length === 0 && (
          <div className="text-center py-16 opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
            <p className="text-4xl mb-3">📋</p>
            <p className="text-sm">{t('mySubmissions.empty')}</p>
            <p className="text-xs mt-1">{t('mySubmissions.emptySub')}</p>
          </div>
        )}

        {submissions.map((s) => {
          const badge = statusBadge(s.status)
          const thumb = s.photos?.[0]?.thumbnail_url ?? s.photos?.[0]?.file_path
          const date = new Date(s.created_at).toLocaleDateString()
          return (
            <div
              key={s.id}
              className="rounded-2xl p-3 flex gap-3"
              style={{ background: 'var(--app-card-bg)' }}
            >
              {/* Thumbnail */}
              <div
                className="w-16 h-16 rounded-xl shrink-0 overflow-hidden flex items-center justify-center"
                style={{ background: 'var(--app-page-bg)' }}
              >
                {thumb ? (
                  <img src={thumb} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl opacity-30">📦</span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                    {s.product_name ?? '—'}
                  </p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {s.status === 'verified' && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: '#22c55e22', color: '#22c55e' }}
                      >
                        +15 🪙
                      </span>
                    )}
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: badge.bg, color: badge.color }}
                    >
                      {badge.label}
                    </span>
                  </div>
                </div>

                {s.shop_name && (
                  <p className="text-xs mt-0.5 truncate opacity-70" style={{ color: 'var(--tg-theme-text-color)' }}>
                    🏪 {s.shop_name}
                  </p>
                )}

                {s.status === 'rejected' && s.comment && (
                  <p className="text-xs mt-0.5" style={{ color: '#ef4444' }}>
                    {t('mySubmissions.reason', { reason: s.comment })}
                  </p>
                )}

                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {(s.price_uzs != null || s.price_usd != null) && (
                    <span className="text-xs font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {s.price_usd != null
                        ? `$${Number(s.price_usd).toLocaleString()}`
                        : `${Number(s.price_uzs).toLocaleString()} UZS`}
                    </span>
                  )}
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full"
                    style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
                  >
                    {s.is_free_submission ? t('common:status.free', { ns: 'common' }) : t('common:status.task', { ns: 'common' })}
                  </span>
                  <span className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                    {date}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
